import { graphql } from '@app/graphql/types';
import client from '@app/utils/client';

const GET_PRESIGN_UPLOAD = graphql(`
  mutation GetManualTransferProofPresignedUpload($onlineOrderID: ID!, $filename: String!) {
    storefront_preSignManualTransferProofUpload(onlineOrderID: $onlineOrderID, filename: $filename) {
      origin
      originPath
      thumbnail
      thumbnailPath
    }
  }
`);

export type UploadedImage = {
  originPath: string;
  thumbnailPath: string;
};

const THUMBNAIL_MAX_DIMENSION = 400;
const THUMBNAIL_QUALITY = 0.8;

async function generateThumbnail(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const ratio = Math.min(
      THUMBNAIL_MAX_DIMENSION / bitmap.width,
      THUMBNAIL_MAX_DIMENSION / bitmap.height,
      1,
    );
    const width = Math.max(1, Math.round(bitmap.width * ratio));
    const height = Math.max(1, Math.round(bitmap.height * ratio));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2D context unavailable');
    }
    ctx.drawImage(bitmap, 0, 0, width, height);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Thumbnail encode failed'))),
        'image/jpeg',
        THUMBNAIL_QUALITY,
      );
    });
  } finally {
    bitmap.close();
  }
}

export async function uploadImage(onlineOrderID: string, file: File): Promise<UploadedImage> {
  const presign = await client.mutate({
    mutation: GET_PRESIGN_UPLOAD,
    variables: { onlineOrderID, filename: file.name },
  });

  const data = presign.data?.storefront_preSignManualTransferProofUpload;
  if (!data) {
    throw new Error('Failed to obtain upload URL');
  }

  // Origin is uploaded as-is so the staff can read every detail of the bank screenshot / scan
  // the QR. Thumbnail is downscaled — it's only used as a list/preview image; the staff still
  // clicks through to the origin for verification.
  let thumbnailBody: Blob = file;
  try {
    thumbnailBody = await generateThumbnail(file);
  } catch {
    // If anything goes wrong (unsupported format, OOM on a huge image, etc.) fall back to the
    // raw file so the upload still succeeds; the only cost is a heavier thumbnail.
  }

  const headers = { 'x-amz-acl': 'public-read' };

  const [originRes, thumbnailRes] = await Promise.all([
    fetch(data.origin, { method: 'PUT', headers, body: file }),
    fetch(data.thumbnail, { method: 'PUT', headers, body: thumbnailBody }),
  ]);

  if (!originRes.ok || !thumbnailRes.ok) {
    throw new Error(
      `Image upload failed (origin: ${originRes.status}, thumbnail: ${thumbnailRes.status})`,
    );
  }

  return { originPath: data.originPath, thumbnailPath: data.thumbnailPath };
}
